import { baseApi } from '@/api/core/baseApi';
import { unwrapApiResponse } from '@/api/patterns/crud';
import type {
  PlatformEmailTemplate,
  PlatformEmailTemplatesResponse,
  GetEmailTemplatesArgs,
  PreviewEmailTemplateArgs,
  SendEmailArgs,
  SendEmailResult,
} from '../types';

export const platformEmailTemplatesApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPlatformEmailTemplates: builder.query<PlatformEmailTemplatesResponse, GetEmailTemplatesArgs | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.isActive !== undefined) params.append('isActive', String(args.isActive));
        if (args?.triggerEvent) params.append('triggerEvent', args.triggerEvent);
        return `/platform/email-templates?${params.toString()}`;
      },
      transformResponse: unwrapApiResponse<PlatformEmailTemplatesResponse>,
      providesTags: [{ type: 'EmailTemplates', id: 'LIST' }],
    }),

    createEmailTemplate: builder.mutation<PlatformEmailTemplate, Partial<PlatformEmailTemplate>>({
      query: (data) => ({
        url: '/platform/email-templates',
        method: 'POST',
        body: data,
      }),
      transformResponse: unwrapApiResponse<PlatformEmailTemplate>,
      invalidatesTags: [{ type: 'EmailTemplates', id: 'LIST' }],
    }),

    updateEmailTemplate: builder.mutation<PlatformEmailTemplate, { id: string; data: Partial<PlatformEmailTemplate> }>({
      query: ({ id, data }) => ({
        url: `/platform/email-templates/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: unwrapApiResponse<PlatformEmailTemplate>,
      invalidatesTags: [{ type: 'EmailTemplates', id: 'LIST' }],
    }),

    deleteEmailTemplate: builder.mutation<void, string>({
      query: (id) => ({
        url: `/platform/email-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'EmailTemplates', id: 'LIST' }],
    }),

    toggleEmailTemplate: builder.mutation<PlatformEmailTemplate, string>({
      query: (id) => ({
        url: `/platform/email-templates/${id}/toggle`,
        method: 'POST',
      }),
      transformResponse: unwrapApiResponse<PlatformEmailTemplate>,
      invalidatesTags: [{ type: 'EmailTemplates', id: 'LIST' }],
    }),

    previewEmailTemplate: builder.mutation<{ htmlContent: string }, PreviewEmailTemplateArgs>({
      query: ({ id, variables }) => ({
        url: `/platform/email-templates/${id}/preview`,
        method: 'POST',
        body: { variables: variables ?? { firstName: 'John', lastName: 'Doe', organizationName: 'Sample Organization' } },
      }),
      transformResponse: unwrapApiResponse<{ htmlContent: string }>,
    }),

    sendEmailTemplate: builder.mutation<SendEmailResult, SendEmailArgs>({
      query: ({ id, recipientType, organizationIds }) => ({
        url: `/platform/email-templates/${id}/send`,
        method: 'POST',
        body: { recipientType, organizationIds },
      }),
      transformResponse: unwrapApiResponse<SendEmailResult>,
    }),
  }),
});

export const {
  useGetPlatformEmailTemplatesQuery,
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useToggleEmailTemplateMutation,
  usePreviewEmailTemplateMutation,
  useSendEmailTemplateMutation,
} = platformEmailTemplatesApi;
